<template>
  <v-app>
    <v-container fluid class="pa-0 fill-height grey lighten-4">
      <v-row no-gutters class="fill-height">
        <!-- Sidebar: Lista de Chats -->
        <v-col
          cols="12"
          md="4"
          class="elevation-2 white"
          style="max-height: 100vh; overflow-y: auto"
        >
          <v-toolbar flat dense>
            <v-text-field
              v-model="search"
              hide-details
              dense
              prepend-inner-icon="mdi-magnify"
              label="Pesquisar"
            />
          </v-toolbar>

          <v-list two-line nav>
            <v-subheader class="grey--text text--darken-1"
              >Conversas</v-subheader
            >
            <v-divider></v-divider>

            <v-list-item
              v-for="chat in filteredChats"
              :key="chat.id"
              :class="{ 'grey lighten-3': chat.id === activeChatId }"
              @click="openChat(chat.id)"
            >
              <v-list-item-avatar color="blue lighten-5">
                <v-icon color="blue">mdi-account</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{
                  chat.name
                }}</v-list-item-title>
                <v-list-item-subtitle class="text-truncate">{{
                  chat.lastMessage.preview
                }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <small class="grey--text">{{
                  formatTime(chat.lastMessage.createdAt)
                }}</small>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>

        <!-- Main: Janela do Chat -->
        <v-col
          cols="12"
          md="8"
          class="d-flex flex-column"
          style="height: 100vh"
        >
          <!-- Header da conversa -->
          <v-toolbar flat dense class="white">
            <v-btn icon class="mr-2 d-md-none" @click="drawer = true"
              ><v-icon>mdi-menu</v-icon></v-btn
            >
            <v-avatar size="32" class="mr-2 blue lighten-5"
              ><v-icon color="blue">mdi-account</v-icon></v-avatar
            >
            <div class="d-flex flex-column">
              <div class="font-weight-medium">
                {{ activeChat?.name || "Selecione um chat" }}
              </div>
              <small class="grey--text">{{ activeChat?.phone || "" }}</small>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click="refreshMessages"
              ><v-icon>mdi-refresh</v-icon></v-btn
            >
          </v-toolbar>

          <!-- Área de mensagens -->
          <div
            ref="scroll"
            class="flex-grow-1 px-3 py-2"
            style="overflow-y: auto; background: #eceff1"
          >
            <div class="text-center my-2">
              <v-btn
                small
                outlined
                @click="loadMore"
                :disabled="loadingMore || !hasMore"
              >
                <v-progress-circular
                  v-if="loadingMore"
                  indeterminate
                  size="16"
                  class="mr-1"
                ></v-progress-circular>
                <span v-else>Carregar mensagens antigas</span>
              </v-btn>
            </div>

            <div
              v-for="msg in activeMessages"
              :key="msg.id"
              class="my-1 d-flex"
              :class="msg.direction === 'out' ? 'justify-end' : 'justify-start'"
            >
              <MessageBubble :message="msg" />
            </div>
          </div>

          <!-- Composer -->
          <v-card flat class="pa-2 white">
            <v-row no-gutters align="center">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on"
                    ><v-icon>mdi-emoticon-happy-outline</v-icon></v-btn
                  >
                </template>
                <v-card class="pa-2" style="max-width: 240px">
                  <div>
                    <v-btn
                      x-small
                      class="ma-1"
                      v-for="e in emojis"
                      :key="e"
                      @click="appendEmoji(e)"
                      >{{ e }}</v-btn
                    >
                  </div>
                </v-card>
              </v-menu>

              <v-btn icon @click="attachMock('image')"
                ><v-icon>mdi-image-outline</v-icon></v-btn
              >
              <v-btn icon @click="attachMock('document')"
                ><v-icon>mdi-file-outline</v-icon></v-btn
              >
              <v-btn icon @click="attachMock('audio')"
                ><v-icon>mdi-microphone</v-icon></v-btn
              >
              <v-btn icon @click="attachMock('location')"
                ><v-icon>mdi-map-marker</v-icon></v-btn
              >

              <v-text-field
                v-model="draft"
                dense
                hide-details
                placeholder="Digite uma mensagem"
                class="mx-2 flex-grow-1"
                @keyup.enter.exact="sendText"
              ></v-text-field>

              <v-btn color="primary" class="ml-1" depressed @click="sendText"
                ><v-icon left>mdi-send</v-icon>Enviar</v-btn
              >
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
// Componente de bolha de mensagem (texto, imagem, áudio, vídeo, doc, localização)
const MessageBubble = {
  name: "MessageBubble",
  props: { message: { type: Object, required: true } },
  computed: {
    isOut() {
      return this.message.direction === "out";
    },
    bubbleColor() {
      return this.isOut ? "blue lighten-5" : "white";
    },
    textColor() {
      return this.isOut ? "blue--text text--darken-3" : "";
    },
  },
  methods: {
    formatTime(ts) {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
  },
  render(h) {
    const m = this.message;
    const base = [
      h("div", { class: "caption grey--text text--darken-1 text-right mb-1" }, [
        this.formatTime(m.createdAt),
      ]),
    ];

    // Conteúdos por tipo
    let content;
    if (m.type === "text") {
      content = h("div", { domProps: { innerText: m.body } });
    } else if (m.type === "image") {
      content = h("div", [
        h("img", {
          attrs: { src: m.mediaUrl, alt: "imagem", width: "220" },
          class: "rounded-lg elevation-1",
        }),
        m.caption ? h("div", { class: "mt-1" }, m.caption) : null,
      ]);
    } else if (m.type === "audio") {
      content = h("audio", { attrs: { controls: true, src: m.mediaUrl } });
    } else if (m.type === "video") {
      content = h("video", {
        attrs: { controls: true, src: m.mediaUrl, width: "260" },
        class: "rounded-lg elevation-1",
      });
    } else if (m.type === "document") {
      content = h("div", [
        h(
          "v-icon",
          { props: { small: true }, class: "mr-1" },
          "mdi-file-outline"
        ),
        h(
          "a",
          { attrs: { href: m.mediaUrl, target: "_blank" } },
          m.filename || "Documento"
        ),
      ]);
    } else if (m.type === "location") {
      const url = `https://www.google.com/maps?q=${m.location.lat},${m.location.lng}`;
      content = h("div", [
        h(
          "v-icon",
          { props: { small: true }, class: "mr-1" },
          "mdi-map-marker"
        ),
        h(
          "a",
          { attrs: { href: url, target: "_blank" } },
          `${m.location.lat.toFixed(5)}, ${m.location.lng.toFixed(5)}`
        ),
      ]);
    } else {
      content = h("div", `[${m.type}] não suportado no mock`);
    }

    return h(
      "div",
      {
        class: ["pa-2", "ma-1", "rounded-lg", this.bubbleColor, "elevation-1"],
        style: { maxWidth: "80%" },
      },
      [content, ...base]
    );
  },
};

export default {
  name: "WhatsappChat",
  components: { MessageBubble },
  data: () => ({
    drawer: false,
    search: "",
    draft: "",
    loadingMore: false,
    hasMore: true,
    emojis: [
      "😀",
      "😁",
      "😂",
      "🤣",
      "😊",
      "😍",
      "👍",
      "🙏",
      "🔥",
      "✨",
      "🍕",
      "🍔",
      "🥤",
    ],

    activeChatId: 1,
    chats: [
      {
        id: 1,
        name: "Diego",
        phone: "+55 49 8888-6059",
        lastMessage: {
          preview: "Beleza, testando o chat!",
          createdAt: Date.now() - 1000 * 60 * 2,
        },
      },
      {
        id: 2,
        name: "Maria",
        phone: "+55 11 9999-0000",
        lastMessage: {
          preview: "Enviei o documento",
          createdAt: Date.now() - 1000 * 60 * 45,
        },
      },
      {
        id: 3,
        name: "Cliente X",
        phone: "+55 48 9999-0000",
        lastMessage: {
          preview: "Localização enviada",
          createdAt: Date.now() - 1000 * 60 * 120,
        },
      },
    ],

    messagesByChat: {
      1: [
        {
          id: "m1",
          direction: "in",
          type: "text",
          body: "Olá! Tudo certo?",
          createdAt: Date.now() - 1000 * 60 * 60,
        },
        {
          id: "m2",
          direction: "out",
          type: "text",
          body: "Beleza, testando o chat!",
          createdAt: Date.now() - 1000 * 60 * 50,
        },
        {
          id: "m3",
          direction: "in",
          type: "image",
          mediaUrl: "https://picsum.photos/300/200",
          caption: "Olha essa imagem",
          createdAt: Date.now() - 1000 * 60 * 40,
        },
        {
          id: "m4",
          direction: "out",
          type: "location",
          location: { lat: -27.09991, lng: -52.61636 },
          createdAt: Date.now() - 1000 * 60 * 35,
        },
        {
          id: "m5",
          direction: "in",
          type: "audio",
          mediaUrl: "https://www.w3schools.com/html/horse.mp3",
          createdAt: Date.now() - 1000 * 60 * 30,
        },
        {
          id: "m6",
          direction: "out",
          type: "document",
          mediaUrl:
            "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
          filename: "Pedido-123.pdf",
          createdAt: Date.now() - 1000 * 60 * 10,
        },
      ],
      2: [
        {
          id: "m21",
          direction: "in",
          type: "text",
          body: "Pode me enviar o contrato?",
          createdAt: Date.now() - 1000 * 60 * 60 * 5,
        },
        {
          id: "m22",
          direction: "out",
          type: "text",
          body: "Enviei agora há pouco.",
          createdAt: Date.now() - 1000 * 60 * 60 * 4.8,
        },
      ],
      3: [
        {
          id: "m31",
          direction: "in",
          type: "location",
          location: { lat: -23.55052, lng: -46.63331 },
          createdAt: Date.now() - 1000 * 60 * 20,
        },
      ],
    },
  }),
  computed: {
    activeChat() {
      return this.chats.find((c) => c.id === this.activeChatId);
    },
    activeMessages() {
      return this.messagesByChat[this.activeChatId] || [];
    },
    filteredChats() {
      const s = (this.search || "").toLowerCase();
      if (!s) return this.chats;
      return this.chats.filter(
        (c) =>
          c.name.toLowerCase().includes(s) ||
          (c.phone || "").toLowerCase().includes(s)
      );
    },
  },
  methods: {
    openChat(id) {
      this.activeChatId = id;
      this.$nextTick(this.scrollToBottom);
    },
    scrollToBottom() {
      const el = this.$refs.scroll;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },
    loadMore() {
      if (this.loadingMore) return;
      this.loadingMore = true;
      // MOCK: adiciona mensagens antigas no topo
      setTimeout(() => {
        const older = [
          {
            id: "old-" + Math.random().toString(36).slice(2),
            direction: "in",
            type: "text",
            body: "Mensagem antiga...",
            createdAt: Date.now() - 1000 * 60 * 120,
          },
          {
            id: "old-" + Math.random().toString(36).slice(2),
            direction: "out",
            type: "text",
            body: "Resposta antiga...",
            createdAt: Date.now() - 1000 * 60 * 118,
          },
        ];
        this.messagesByChat[this.activeChatId] = [
          ...older,
          ...this.activeMessages,
        ];
        this.loadingMore = false;
        this.hasMore = this.activeMessages.length < 200;
      }, 600);
    },
    sendText() {
      const text = (this.draft || "").trim();
      if (!text) return;
      const msg = {
        id: "local-" + Date.now(),
        direction: "out",
        type: "text",
        body: text,
        createdAt: Date.now(),
      };
      this.messagesByChat[this.activeChatId] = [...this.activeMessages, msg];
      this.draft = "";
      this.$nextTick(this.scrollToBottom);

      // TODO: aqui entraria a chamada para sua API + emissão socket
    },
    appendEmoji(e) {
      this.draft = (this.draft || "") + e;
    },
    attachMock(kind) {
      const base = {
        id: "local-" + Math.random().toString(36).slice(2),
        direction: "out",
        createdAt: Date.now(),
      };
      let msg;
      if (kind === "image")
        msg = {
          ...base,
          type: "image",
          mediaUrl: "https://picsum.photos/300/200",
          caption: "Imagem mock",
        };
      if (kind === "document")
        msg = {
          ...base,
          type: "document",
          mediaUrl:
            "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
          filename: "Arquivo.pdf",
        };
      if (kind === "audio")
        msg = {
          ...base,
          type: "audio",
          mediaUrl: "https://www.w3schools.com/html/horse.mp3",
        };
      if (kind === "location")
        msg = {
          ...base,
          type: "location",
          location: { lat: -27.59538, lng: -48.54805 },
        };
      if (kind && msg) {
        this.messagesByChat[this.activeChatId] = [...this.activeMessages, msg];
        this.$nextTick(this.scrollToBottom);
      }
    },
    refreshMessages() {
      // MOCK: em um real, chamaria sua API para sincronizar
      this.$nextTick(this.scrollToBottom);
    },
    formatTime(ts) {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
  },
  mounted() {
    this.$nextTick(this.scrollToBottom);
  },
};
</script>

<style scoped>
.fill-height {
  height: 100vh;
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rounded-lg {
  border-radius: 16px;
}
</style>
